import React, { Suspense, lazy, useMemo, useState, useEffect, useCallback } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import Navbar from './components/Navbar';
import AiSettings from './components/AiSettings';
import { AiSettingsContext } from './lib/aiSettingsContext';
import { getSavedNames, getActiveProfile, setActiveProfile as storeActiveProfile } from './lib/chartStorage';
import { trackPageView, trackProfileSwitch } from './lib/analytics';
import { restoreFromIdb } from './lib/bpStorage';

// Lazy-load all pages — only loads when navigated to
const Home = lazy(() => import('./pages/Home'));
const ChartPage = lazy(() => import('./pages/ChartPage'));
const GrahaGuide = lazy(() => import('./pages/GrahaGuide'));
const RashiGuide = lazy(() => import('./pages/RashiGuide'));
const NakshatraGuide = lazy(() => import('./pages/NakshatraGuide'));
const VargaGuide = lazy(() => import('./pages/VargaGuide'));
const BigPicture = lazy(() => import('./pages/BigPicture'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Prashna = lazy(() => import('./pages/Prashna'));
const About = lazy(() => import('./pages/About'));

function PageLoader() {
  return (
    <div className="page-loader">
      <div className="page-loader-spinner"></div>
    </div>
  );
}

function App() {
  const [activeProfile, setActiveProfile] = useState(getActiveProfile());
  const [savedNames, setSavedNames] = useState(getSavedNames());
  const [aiSettingsOpen, setAiSettingsOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Single context value so anywhere in the tree (Navbar gear icon,
  // AiQuestionBox prompts, SectionInsight panels, BigPicture chat) can
  // pop the same drawer.
  const aiSettingsCtx = useMemo(
    () => ({ open: () => setAiSettingsOpen(true) }),
    [],
  );

  // Track page views on route change
  useEffect(() => {
    trackPageView(location.pathname, document.title);
  }, [location.pathname]);

  // On first mount, restore any data from IndexedDB that's missing from
  // localStorage (e.g. if the user cleared site data). Background task —
  // pages render immediately from whatever localStorage already has, and
  // the next render after restore picks up anything we recovered.
  useEffect(() => {
    restoreFromIdb().then(() => {
      // Refresh the saved-charts list and active profile in case the
      // restore brought them back.
      setSavedNames(getSavedNames());
      const restoredActive = getActiveProfile();
      if (restoredActive && restoredActive !== activeProfile) {
        setActiveProfile(restoredActive);
      }
    });
    // eslint-disable-next-line
  }, []);

  const refreshNames = useCallback(() => {
    setSavedNames(getSavedNames());
  }, []);

  // Pages that consume `activeProfile` and re-render their own context when
  // it changes — switching profiles on these pages should NOT yank the user
  // back to /chart. `/` is profile-aware too: it shows the Dashboard when a
  // chart is selected and the landing page otherwise.
  const PROFILE_AWARE_PATHS = ['/', '/chart', '/big-picture', '/dashboard'];

  const handleProfileChange = useCallback((name) => {
    setActiveProfile(name);
    storeActiveProfile(name);
    if (name) {
      trackProfileSwitch(name);
      if (!PROFILE_AWARE_PATHS.includes(location.pathname)) {
        navigate('/chart');
      }
    }
  }, [navigate, location.pathname]);

  const handleChartSaved = useCallback((name) => {
    refreshNames();
    setActiveProfile(name);
    storeActiveProfile(name);
  }, [refreshNames]);

  const handleProfileDeleted = useCallback(() => {
    refreshNames();
    setActiveProfile('');
    storeActiveProfile('');
  }, [refreshNames]);

  return (
    <AiSettingsContext.Provider value={aiSettingsCtx}>
      <div className="app">
        <Navbar
          savedNames={savedNames}
          activeProfile={activeProfile}
          onProfileChange={handleProfileChange}
        />
        <main className="main-content">
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* `/` always renders the landing page so it's never hidden
                  from anyone. Returning users with a chart see a tile on the
                  landing that takes them to /dashboard ("their Home"). The
                  brand logo also points here. */}
              <Route path="/" element={<Home savedNames={savedNames} />} />
              <Route path="/welcome" element={<Home savedNames={savedNames} />} />
              <Route path="/chart" element={
                <ChartPage
                  activeProfile={activeProfile}
                  onChartSaved={handleChartSaved}
                  onProfileDeleted={handleProfileDeleted}
                />
              } />
              <Route path="/graha" element={<GrahaGuide />} />
              <Route path="/rashi" element={<RashiGuide />} />
              <Route path="/nakshatra" element={<NakshatraGuide />} />
              <Route path="/varga" element={<VargaGuide />} />
              <Route path="/big-picture" element={<BigPicture activeProfile={activeProfile} />} />
              <Route path="/dashboard" element={<Dashboard activeProfile={activeProfile} />} />
              <Route path="/prashna" element={<Prashna />} />
              <Route path="/about" element={<About />} />
            </Routes>
          </Suspense>
        </main>
        <footer className="footer">
          <p>AntarDristi &middot; South Indian Astrology</p>
        </footer>
        {/* Single drawer instance — accessible from anywhere via the
            navbar gear icon or the in-page "Open AI Settings" prompts. */}
        <AiSettings isOpen={aiSettingsOpen} onClose={() => setAiSettingsOpen(false)} />
      </div>
    </AiSettingsContext.Provider>
  );
}

export default App;
